// STEP1-OUTBOX in the in-memory API: Workforce › Outbox (services/api/src/routes/outbox.ts).
// The fake company has no outward acts (no sample data): the list is empty and an item id is
// 404, as on a real company before any approved external action. Tests add rows with `add`.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, problem } from "./fake";

export type FakeOutboxItem = OperationOutput<"outbox.list">["data"][number];

export const FAKE_OUTBOX_ROUTES = [
  "outbox.list",
  "outbox.retry",
  "outbox.cancel",
] as const satisfies readonly RouteId[];

export function createOutboxFake(deps: { now: () => Date }) {
  const items: FakeOutboxItem[] = [];
  const find = (companyId: string, id: string) =>
    items.find((x) => x.company_id === companyId && x.id === id) ?? null;

  const handlers: Partial<Record<RouteId, Handler>> = {
    "outbox.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const status = url.searchParams.get("status");
      const group = url.searchParams.get("group");
      const item = url.searchParams.get("item");
      const connection = url.searchParams.get("connection");
      const requestedBy = url.searchParams.get("requested_by");
      const approval = url.searchParams.get("approval");
      const groups: Record<string, readonly string[]> = {
        pending: ["queued", "sending"],
        sent: ["sent"],
        failed: ["failed", "dead"],
      };
      const data = items
        .filter(
          (x) =>
            x.company_id === principal.company.id &&
            (!status || x.status === status) &&
            (!group || (groups[group] ?? []).includes(x.status)) &&
            (!item || x.id === item) &&
            (!connection || x.connection_id === connection) &&
            (!requestedBy || x.requested_by_membership_id === requestedBy) &&
            (!approval || x.approval_id === approval),
        )
        .sort((a, b) => (a.id < b.id ? 1 : -1));
      return json({ data, next_cursor: null, prev_cursor: null });
    },
    "outbox.retry": ({ principal, params }) => {
      if (!principal) return notFound();
      const x = find(principal.company.id, params.id ?? "");
      if (!x) return notFound();
      if (x.status !== "dead" && x.status !== "failed")
        return problem(409, "outbox_state_conflict", "Not possible in the outbox item's state");
      Object.assign(x, {
        status: "queued",
        dead_reason: null,
        attempts: x.status === "dead" ? 0 : x.attempts,
        next_attempt_at: deps.now().toISOString(),
        updated_at: deps.now().toISOString(),
      });
      return json(x);
    },
    "outbox.cancel": ({ principal, params }) => {
      if (!principal) return notFound();
      const x = find(principal.company.id, params.id ?? "");
      if (!x) return notFound();
      if (x.status === "sent" || x.status === "cancelled" || x.status === "sending")
        return problem(409, "outbox_state_conflict", "Not possible in the outbox item's state");
      Object.assign(x, {
        status: "cancelled",
        dead_reason: null,
        cancelled_at: deps.now().toISOString(),
        updated_at: deps.now().toISOString(),
      });
      return json(x);
    },
  };

  return {
    handlers,
    /** Tests: put an item in a company's outbox. */
    add: (item: FakeOutboxItem) => items.push(item),
  };
}
